import React, { useCallback, useMemo, useState } from 'react';
import { IconButton } from '@mui/material';
import { toast } from 'react-toastify';

import { PLACEMENT } from '@components/CustomTextInput';
import ResourcesList from '@shared/Resources/shared/components/ResourcesList';
import {
  bulkCreateSentences,
  deleteSentence,
  fetchSentenceDetails,
  fetchSentences,
  updateSentence,
} from '@services/sentencesService';
import { exportSentencesCSV, parseSentencesCSV } from '../sentencesCSV';
import {
  StyledCustomTextInput as HeaderStyledCustomTextInput,
  StyledSearchIcon as HeaderStyledSearchIcon,
} from '@containers/Admin/containers/ResourceManager/components/ResourceHeader/styles';
import { createSentencesTableConf } from '../config';
import { SentencesRoot } from '../styles';
import {
  ExportIcon,
  HiddenFileInput,
  ImportIcon,
  ListArea,
  Toolbar,
} from '@shared/Resources/Stimulus/shared/tabStyles';
import Text, { TEXT_COLOR, TEXT_SIZE, TEXT_WEIGHT } from '@components/Text';
import TextArea from '@components/TextArea';
import { POPULATION_OPTIONS } from '../../SpeechTopics/config';
import { formatPopulation, formatDateTime, formatWordWithSyllables } from '@shared/Resources/Stimulus/shared/formatters';
import { ContentRow, DetailsBody, DetailsPane } from '@shared/Resources/Stimulus/shared/detailsPaneStyles';
import { useCsvResourceTab } from '@shared/Resources/Stimulus/shared/useCsvResourceTab';
import { TOAST_OPTIONS, toToastMessage } from '@shared/Resources/Stimulus/shared/toastUtils';
import { applyOptimisticSave } from '@shared/Resources/Stimulus/shared/optimisticSave';
import StimulusSentenceDetailsEditor from '@shared/Resources/Stimulus/shared/StimulusSentenceDetailsEditor';
import DeleteConfirmDialog from '@shared/Resources/Stimulus/shared/DeleteConfirmDialog';

const SentencesList = () => {
  const [saveLoading, setSaveLoading] = useState(false);

  const {
    filteredItems: filtered,
    search,
    setSearch,
    loading,
    selectedItem: selectedSentence,
    details: selectedDetails,
    detailsLoading,
    setSelectedItem,
    setItems,
    reloadDetails,
    fileInputRef,
    handleClickImport,
    handleImportCSV,
    handleExportCSV,
    handleDelete,
    handleConfirmDelete,
    handleCancelDelete,
    pendingDeleteId,
    handleSelect: handleSelectSentence,
  } = useCsvResourceTab({
    fetchItems: fetchSentences,
    parseCsvFile: parseSentencesCSV,
    bulkCreate: bulkCreateSentences,
    exportCsv: exportSentencesCSV,
    deleteItem: deleteSentence,
    entityLabel: 'sentences',
    enableSelection: true,
    fetchDetails: fetchSentenceDetails,
    messages: {
      loadFailed: 'Failed to load sentences',
      detailsFailed: 'Failed to load sentence details',
      imported: (count) => `Imported ${count} sentences`,
      exported: (count) => `Exported ${count} sentences`,
      deleted: 'Deleted',
    },
  });

  const tableConf = useMemo(() => createSentencesTableConf({ onDelete: handleDelete }), [handleDelete]);

  const handleSaveSentence = useCallback(
    async (input) => {
      const saveSucceeded = await applyOptimisticSave({
        selectedItem: selectedSentence,
        input,
        setLoading: setSaveLoading,
        setItems,
        setSelectedItem,
        updateItem: updateSentence,
        successMessage: 'Sentence',
        errorMessage: 'Failed to update sentence',
        onSuccess: (label) => toast.success(`${label} updated`, TOAST_OPTIONS),
        onError: (err, fallback) => toast.error(toToastMessage(err, fallback), TOAST_OPTIONS),
      });

      if (saveSucceeded) {
        await reloadDetails();
      }

      return saveSucceeded;
    },
    [reloadDetails, selectedSentence, setItems, setSelectedItem]
  );

  const populationLabel = useMemo(
    () => formatPopulation(selectedDetails?.population, POPULATION_OPTIONS),
    [selectedDetails?.population]
  );

  const detailsDir = selectedDetails?.locale === 'HE' ? 'rtl' : 'ltr';

  const syllablePreviewLines = useMemo(() => {
    const words = selectedDetails?.words || [];
    return words.map((w) => formatWordWithSyllables(w));
  }, [selectedDetails?.words]);

  const renderToolbar = () => (
    <Toolbar>
      <HeaderStyledCustomTextInput
        placeholder="Search by word in text..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        Icon={() => <HeaderStyledSearchIcon />}
        iconPlacement={PLACEMENT.RIGHT}
        autofocus
        name="sentences-search"
        id="sentences-search"
      />
      <IconButton aria-label="Import from CSV" title="Import from CSV" onClick={handleClickImport} size="small">
        <ImportIcon />
      </IconButton>
      <IconButton aria-label="Download CSV" title="Download CSV" onClick={handleExportCSV} size="small">
        <ExportIcon />
      </IconButton>
      <HiddenFileInput type="file" accept=".csv" ref={fileInputRef} onChange={handleImportCSV} />
    </Toolbar>
  );

  if (loading) return <div>Loading...</div>;

  return (
    <>
      <SentencesRoot>
        {renderToolbar()}
        <ContentRow>
          <ListArea>
            <ResourcesList
              items={filtered}
              tableConf={tableConf}
              selectedItem={selectedSentence}
              onSelect={handleSelectSentence}
              showSelectionArrow={false}
            />
          </ListArea>
          <DetailsPane>
            <DetailsBody>
              {!selectedSentence ? (
                <Text size={TEXT_SIZE.H4} textColor={TEXT_COLOR.DISABLED}>
                  Select a sentence to view details
                </Text>
              ) : detailsLoading ? (
                <Text size={TEXT_SIZE.H4} textColor={TEXT_COLOR.DISABLED}>
                  Loading...
                </Text>
              ) : !selectedDetails ? (
                <Text size={TEXT_SIZE.H4} textColor={TEXT_COLOR.DISABLED}>
                  No details
                </Text>
              ) : (
                <StimulusSentenceDetailsEditor
                  item={selectedDetails}
                  onSave={handleSaveSentence}
                  saving={saveLoading}
                />
              )}
            </DetailsBody>
          </DetailsPane>
        </ContentRow>
      </SentencesRoot>
      {pendingDeleteId && (
        <DeleteConfirmDialog
          message="Are you sure you want to delete this sentence?"
          onConfirm={handleConfirmDelete}
          onCancel={handleCancelDelete}
        />
      )}
    </>
  );
};

export default SentencesList;
